import { ClipboardCheck, Phone, Siren } from "lucide-react";
import { Button } from "@/components/ui/button";
import { localizePath, type LocaleCode, useI18n } from "@/i18n";
import { business } from "@/config/business";
import { trackAnalyticsEvent } from "@/lib/analytics";

const STICKY_COPY: Record<
  LocaleCode,
  { call: string; urgent: string; estimate: string; actions: string }
> = {
  "en-ca": {
    call: "Call",
    urgent: "Urgent Help",
    estimate: "Free Estimate",
    actions: "Mobile actions",
  },
  "zh-cn": { call: "致电", urgent: "紧急帮助", estimate: "免费估价", actions: "移动操作" },
  "zh-tw": { call: "致電", urgent: "緊急協助", estimate: "免費估價", actions: "行動操作" },
  "pa-in": {
    call: "ਕਾਲ",
    urgent: "ਫੌਰੀ ਮਦਦ",
    estimate: "ਮੁਫ਼ਤ ਅੰਦਾਜ਼ਾ",
    actions: "ਮੋਬਾਈਲ ਕਾਰਵਾਈਆਂ",
  },
  "ko-kr": { call: "전화", urgent: "긴급 도움", estimate: "무료 견적", actions: "모바일 작업" },
  "fa-ir": {
    call: "تماس",
    urgent: "کمک فوری",
    estimate: "برآورد رایگان",
    actions: "اقدامات موبایل",
  },
  "ar-sa": {
    call: "اتصال",
    urgent: "مساعدة عاجلة",
    estimate: "تقدير مجاني",
    actions: "إجراءات الجوال",
  },
};

export function StickyCta() {
  const { locale } = useI18n();
  const copy = STICKY_COPY[locale];
  const actionClass =
    "h-auto min-h-14 flex-col gap-1 px-1.5 py-2 text-[0.68rem] leading-none sm:flex-row sm:gap-2 sm:text-xs";

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/96 shadow-[0_-12px_30px_-20px_rgba(15,23,42,0.45)] backdrop-blur lg:hidden">
      <nav
        aria-label={copy.actions}
        className="container-page grid grid-cols-3 gap-1.5 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
      >
        <Button variant="quiet" asChild className={actionClass}>
          <a
            href={business.phoneHref}
            onClick={() =>
              trackAnalyticsEvent({ event: "phone_click", link_location: "mobile_sticky" })
            }
          >
            <Phone aria-hidden="true" />
            {copy.call}
          </a>
        </Button>
        <Button variant="brand" asChild className={actionClass}>
          <a
            href={localizePath("/emergency-roofing", locale)}
            onClick={() =>
              trackAnalyticsEvent({
                event: "emergency_cta_click",
                cta_location: "mobile_sticky_urgent",
                lead_type: "emergency",
              })
            }
          >
            <Siren aria-hidden="true" />
            {copy.urgent}
          </a>
        </Button>
        <Button variant="accent" asChild className={actionClass}>
          <a
            href={localizePath("/roof-estimate", locale)}
            onClick={() =>
              trackAnalyticsEvent({
                event: "estimate_cta_click",
                cta_location: "mobile_sticky_estimate",
                lead_type: "planned",
              })
            }
          >
            <ClipboardCheck aria-hidden="true" />
            {copy.estimate}
          </a>
        </Button>
      </nav>
    </div>
  );
}
